import { AxiosError } from 'axios';
import { APIActions, Button, CustomDialog } from 'web-library';
import IconWithLabel from 'web-library/stories/atom/IconWithLabel/IconWithLabel';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import React, { useState } from 'react';
import { MutationStatus } from 'react-query';
import { IStylingAndTranslationHooks } from './model';
import { PutStopFileProcessingRequest } from '../../../../api-refactored/group-exam-registration/usePutStopFileProcessing';
import { UseCustomMutationReturn } from '../../../../api-refactored/utils/useCustomMutation';
import { UseCustomQueryReturn } from '../../../../api-refactored/utils/useCustomQuery';
import { GroupRegistrationSummaryResponse } from '../../../../api-refactored/group-exam-registration/useGetRegistrationSummary';
import { GenericErrorResponse } from '../../../../api-refactored/typings';
import { FileProcessingState } from '../../../../models/File';
import { GetDocumentUploadStatusResponse } from '../../../../api-refactored/document/useGetDocumentUploadStatus';

interface IStartFileProcessingActionLabel extends IStylingAndTranslationHooks {
  apiHooks: {
    stopFileProcessingMutation: UseCustomMutationReturn<
      void,
      AxiosError<GenericErrorResponse>,
      PutStopFileProcessingRequest,
      unknown
    >;
    getGroupRegistrationSummaryQuery: UseCustomQueryReturn<
      GroupRegistrationSummaryResponse,
      AxiosError<GenericErrorResponse>
    >;
    getDocumentUploadStatus: UseCustomQueryReturn<
      GetDocumentUploadStatusResponse,
      AxiosError<GenericErrorResponse>
    >;
  };
  applicationUuid: string;
}

const StartFileProcessingVisualLabel = ({
  classes,
  translation: t,
}: IStylingAndTranslationHooks) => (
  <IconWithLabel
    className={classes.uploadingIcon}
    iconName={IconName.loadingIndicator}
    iconSize="M"
    label={t('exam->>UploadExcelTemplate.ProcessingInProgressDescription')}
    typographyProps={{
      variant: 'h5',
      color: 'inherit',
    }}
  />
);

const StartFileProcessingActionLabel = ({
  classes,
  translation: t,
  apiHooks,
  applicationUuid,
}: IStartFileProcessingActionLabel) => {
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const {
    mutate: stopFileProcessingMutate,
    status: stopFileProcessingStatus,
  } = apiHooks.stopFileProcessingMutation;
  const {
    refetch: summaryRefetch,
    data: summaryData,
    status: getSummaryStatus,
  } = apiHooks.getGroupRegistrationSummaryQuery;
  const isStopProcessingReady =
    summaryData?.fileStatus !== FileProcessingState.SUCCESS && summaryData?.fileUuid;
  const stopFileProcessingClickHandler = () => {
    setIsConfirmationModalOpen(true);
  };

  const customizeMutationStatus = (): MutationStatus => {
    if (!isStopProcessingReady) {
      return 'loading';
    }
    if (stopFileProcessingStatus === 'error' || getSummaryStatus === 'error') {
      return 'error';
    }
    return 'idle';
  };

  return (
    <>
      <CustomDialog
        open={isConfirmationModalOpen}
        title={t('exam->>UploadExcelTemplate.StopFileProcessingTitle')}
        message={t('exam->>UploadExcelTemplate.StopFileProcessingDescription')}
        action={
          <APIActions
            onClose={() => setIsConfirmationModalOpen(false)}
            apiCall={async () => {
              await summaryRefetch();
              if (isStopProcessingReady) {
                // @ts-ignore
                stopFileProcessingMutate({ fileUuid: summaryData?.fileUuid, applicationUuid });
                setIsConfirmationModalOpen(false);
              }
            }}
            apiStatus={customizeMutationStatus()}
            cancelString={t('exam->>UploadExcelTemplate.Cancel')}
            apiString={t('exam->>UploadExcelTemplate.Proceed')}
          />
        }
      />
      <Button
        variant="text"
        className={classes.stopProcessingButton}
        onClick={stopFileProcessingClickHandler}
        id="button-stop-processing"
      >
        <IconWithLabel
          className={classes.stopProcessingIcon}
          iconName={IconName.cross}
          iconSize="M"
          label={t('exam->>UploadExcelTemplate.StopProcessing')}
          typographyProps={{
            variant: 'h5',
            color: 'inherit',
          }}
        />
      </Button>
    </>
  );
};

export { StartFileProcessingVisualLabel, StartFileProcessingActionLabel };
